<template>
    <section class="ButtonExample">
        <div class="ButtonExample__header">
            <div>
                <h2 class="ButtonExample__title">
                    NewButton
                </h2>
                <p class="ButtonExample__desc">
                    Variants: gradient, fill, outline, flat, link · Colors: primary, secondary, danger, neutral
                </p>
            </div>
            <div class="ButtonExample__toggle">
                <button
                    class="ButtonExample__toggleBtn"
                    :class="{ 'ButtonExample__toggleBtn--active': !showPlayground }"
                    @click="showPlayground = false"
                >
                    Gallery
                </button>
                <button
                    class="ButtonExample__toggleBtn"
                    :class="{ 'ButtonExample__toggleBtn--active': showPlayground }"
                    @click="showPlayground = true"
                >
                    Playground
                </button>
            </div>
        </div>

        <template v-if="!showPlayground">
            <div
                v-for="variant in variants"
                :key="variant"
                class="ButtonExample__row"
            >
                <span class="ButtonExample__row-label">{{ variant }}</span>
                <div class="ButtonExample__row-buttons">
                    <div v-for="color in colors" :key="color">
                        <span
                            v-if="!isButtonSupported(variant, color)"
                            class="ButtonExample__unsupported"
                        >
                            Unsupported
                        </span>
                        <NewButton
                            v-else
                            :key="`${variant}-${color}`"
                            :variant="variant"
                            :color="color"
                            class="ButtonExample__button"
                        >
                            {{ color }}
                        </NewButton>
                    </div>
                </div>
            </div>

            <h3 class="ButtonExample__subsection-title">
                Sizes
            </h3>
            <div class="ButtonExample__size-controller">
                <label class="ButtonExample__controller-label">
                    Variant
                    <select v-model="sizeVariant" class="ButtonExample__select">
                        <option v-for="v in variants" :key="v" :value="v">
                            {{ v }}
                        </option>
                    </select>
                </label>
                <label class="ButtonExample__controller-label">
                    Color
                    <select v-model="sizeColor" class="ButtonExample__select">
                        <option v-for="c in colors" :key="c" :value="c">
                            {{ c }}
                        </option>
                    </select>
                </label>
            </div>
            <div class="ButtonExample__size-columns">
                <table class="ButtonExample__size-table">
                    <thead>
                        <tr>
                            <th class="ButtonExample__size-th">
                                Size
                            </th>
                            <th class="ButtonExample__size-th">
                                Text
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="row in textButtonRows"
                            :key="row.size"
                            class="ButtonExample__size-tr"
                        >
                            <td class="ButtonExample__size-td ButtonExample__size-td--label">
                                {{ row.size }}
                            </td>
                            <td class="ButtonExample__size-td">
                                <NewButton :variant="sizeVariant" :color="sizeColor" :size="row.size">
                                    Button
                                </NewButton>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <table class="ButtonExample__size-table">
                    <thead>
                        <tr>
                            <th class="ButtonExample__size-th">
                                Size
                            </th>
                            <th class="ButtonExample__size-th">
                                Icon
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="row in iconButtonRows"
                            :key="row.size"
                            class="ButtonExample__size-tr"
                        >
                            <td class="ButtonExample__size-td ButtonExample__size-td--label">
                                {{ row.size }}
                            </td>
                            <td class="ButtonExample__size-td">
                                <NewButton :variant="sizeVariant" :color="sizeColor" :size="row.size">
                                    <EidIcon :icon-src="faAngleLeft" />
                                </NewButton>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>

        <ComponentPlayground
            v-else
            :component="NewButton"
            :defaults="playgroundDefaults"
            :prop-custom-type-labels="{
                variant: 'string => gradient | fill | outline | flat | link',
                color: 'string => primary | secondary | danger | neutral',
                size: 'string => lg | default | sm | xs | icon | icon-sm | icon-xs | icon-xxs',
            }"
            description="Interactive playground — edit variant, color, size, and disabled state live."
        >
            <template #default="{ liveProps }">
                <NewButton v-bind="liveProps">
                    Button
                </NewButton>
            </template>
        </ComponentPlayground>
    </section>
</template>

<style lang="scss" scoped>
.ButtonExample {
    padding: 1.5rem;
    max-width: 56rem;
    background: #fff;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.ButtonExample__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1.25rem;
}

.ButtonExample__toggle {
    display: flex;
    flex-shrink: 0;
    border: 1px solid var(--eidTechGray-25);
    border-radius: 0.375rem;
    overflow: hidden;
}

.ButtonExample__toggleBtn {
    padding: 0.3rem 0.75rem;
    font-size: 0.8125rem;
    font-weight: 500;
    background: none;
    border: none;
    cursor: pointer;
    color: var(--eidTechGray-75);

    &:hover:not(&--active) {
        background: var(--eidTechGray-10);
    }

    &--active {
        background: var(--eidTechGray-10);
        color: var(--eidTechGray);
    }
}

.ButtonExample__title {
    margin: 0 0 0.25rem;
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--eidTechGray);
}

.ButtonExample__desc {
    margin: 0;
    font-size: 0.8125rem;
    color: var(--eidTechGray-75);
}

.ButtonExample__subsection-title {
    margin: 1.5rem 0 0.25rem;
    font-size: 1rem;
    font-weight: 600;
    color: var(--eidTechGray);
}

.ButtonExample__subsection-desc {
    margin: 0 0 0.75rem;
}

.ButtonExample__size-controller {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 1rem;
    margin-bottom: 0.75rem;
}

.ButtonExample__controller-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--eidTechGray-75);
}

.ButtonExample__select {
    padding: 0.25rem 0.5rem;
    font-size: 0.8125rem;
    border: 1px solid var(--eidTechGray-10);
    border-radius: 0.25rem;
    background: #fff;
    color: var(--eidTechGray);
}

.ButtonExample__row {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.75rem 0;
    border-bottom: 1px solid var(--eidTechGray-10);

    &:last-child {
        border-bottom: none;
    }
}

.ButtonExample__row-label {
    flex-shrink: 0;
    width: 5rem;
    font-size: 13px;
    font-weight: 500;
    color: var(--eidTechGray-75);
    text-transform: capitalize;
}

.ButtonExample__row-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.ButtonExample__button {
    flex-shrink: 0;
}

.ButtonExample__unsupported {
    display: inline-flex;
    align-items: center;
    padding: 0.5rem 0.75rem;
    font-size: 13px;
    color: var(--eidTechGray-75);
    font-style: italic;
}

.ButtonExample__size-columns {
    display: flex;
    align-items: flex-start;
    gap: 48px;
    margin-top: 8px;
}

.ButtonExample__size-table {
    border-collapse: collapse;
}

.ButtonExample__size-th {
    padding: 6px 24px 6px 0;
    font-size: 13px;
    font-weight: 500;
    color: var(--eidTechGray-75);
    font-family: monospace;
    text-align: left;
}

.ButtonExample__size-tr {
    border-top: 1px solid var(--eidTechGray-10);
}

.ButtonExample__size-td {
    padding: 10px 24px 10px 0;
    vertical-align: middle;

    &--label {
        font-size: 13px;
        font-weight: 500;
        color: var(--eidTechGray-75);
        font-family: monospace;
        white-space: nowrap;
        min-width: 64px;
    }
}
</style>

<script lang="ts">
    import { defineComponent } from 'vue';

    import { faAngleLeft } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';

    import NewButton from '@/components/new-button/NewButton.vue';

    import ComponentPlayground from './ComponentPlayground.vue';

    const VARIANTS = [ 'gradient', 'fill', 'outline', 'flat', 'link' ] as const;
    const COLORS = [ 'primary', 'secondary', 'danger', 'neutral' ] as const;
    const UNSUPPORTED_BUTTONS = {
        gradient: {
            secondary: true,
            danger: true,
            neutral: true,
        },
    } as Record<string, Record<string, boolean>>;

    const TEXT_BUTTON_ROWS = [
        { size: 'lg' as const },
        { size: 'default' as const },
        { size: 'sm' as const },
        { size: 'xs' as const },
    ];

    const ICON_BUTTON_ROWS = [
        { size: 'icon' as const },
        { size: 'icon-sm' as const },
        { size: 'icon-xs' as const },
        { size: 'icon-xxs' as const },
    ];

    export default defineComponent({
        name: 'ButtonExample',
        components: { NewButton, EidIcon, ComponentPlayground },
        data() {
            return {
                NewButton,
                showPlayground: false,
                variants: VARIANTS,
                colors: COLORS,
                textButtonRows: TEXT_BUTTON_ROWS,
                iconButtonRows: ICON_BUTTON_ROWS,
                sizeVariant: 'fill' as const,
                sizeColor: 'primary' as const,
                faAngleLeft,
                playgroundDefaults: {
                    variant: 'fill' as typeof VARIANTS[number],
                    color: 'primary' as typeof COLORS[number],
                    size: 'default' as const,
                    disabled: false,
                },
            };
        },
        methods: {
            isButtonSupported(variant: string, color: string): boolean {
                const unsupportedVariant = UNSUPPORTED_BUTTONS[variant];
                if (!unsupportedVariant) {
                    return true;
                }
                return !unsupportedVariant[color];
            },
        },
    });
</script>
